import { useMemo } from "react";
import type { DigitalSampleImage } from "../components/digitalSamples/DigitalSampleImageGallery";
import { createAxiosInstance } from "../service/axios-instance.service";
import {
  type RequestState,
  useAuthenticatedRequest,
  useRequest,
} from "./request";

export interface DigitalSample {
  id: number;
  creationDate: string;
  materialFinish: string;
  assets: Array<DigitalSampleAsset>;
  isPublished: boolean;
}

export interface DigitalSampleAsset {
  id: string;
  type: string;
  assetUrl: string;
  fileName: string;
  tags: Array<DigitalSampleAssetTag>;
}

export type DigitalSampleAssetTag =
  | "IMAGE"
  | "MATERIAL"
  | "SMALL"
  | "LARGE"
  | "LANDSCAPE"
  | "THUMBNAIL"
  | "SQUARE"
  | "INTERNAL"
  | "COMPLIANT"
  | "PROCESSED";

export function useDigitalSample(
  filter: {
    eljExperimentId?: string;
    id?: string;
  },
  shouldLoad: boolean,
): RequestState<Array<DigitalSample>> {
  const config = useMemo(() => {
    const p = new URLSearchParams();
    p.set("sampleStatus", "ACTIVE");
    if (filter.eljExperimentId) {
      p.set("eljExperimentId", filter.eljExperimentId);
    }
    if (filter.id) {
      p.set("id", filter.id);
    }
    return { params: p };
  }, [filter.eljExperimentId, filter.id]);
  const [result, error, isLoading] = useAuthenticatedRequest<{
    items: Array<DigitalSample>;
  }>("/v0/digital-sample-management/digital-samples", config, shouldLoad);
  return [result?.items, error, isLoading];
}

const publicAxios = createAxiosInstance({});

export function usePublicDigitalSample(
  filter: {
    eljExperimentId?: string;
  },
  shouldLoad: boolean,
): RequestState<Array<DigitalSample>> {
  const config = useMemo(() => {
    const p = new URLSearchParams();
    if (filter.eljExperimentId) {
      p.set("eljExperimentId", filter.eljExperimentId);
    }
    return { params: p };
  }, [filter.eljExperimentId]);
  const [result, error, isLoading] = useRequest<{
    items: Array<DigitalSample>;
  }>("/v0/public-digital-samples", publicAxios, config, shouldLoad);
  return [result?.items, error, isLoading];
}

export function useImage(
  digitalSamples?: Array<DigitalSample>,
  assetTag: DigitalSampleAssetTag = "THUMBNAIL",
  highResolution = false,
): DigitalSampleAsset | undefined {
  const sample: DigitalSample | undefined = sortDigitalSamples(
    digitalSamples ?? [],
  )[0];
  return sample?.assets
    ?.filter((asset) => asset.tags.includes(assetTag))
    ?.filter((asset) =>
      highResolution
        ? asset.tags.includes("LARGE")
        : !asset.tags.includes("LARGE"),
    )[0];
}

export function useDigitalSampleGalleryImages(
  digitalSamples?: Array<DigitalSample>,
): Array<DigitalSampleImage> {
  const sorted = sortDigitalSamples(digitalSamples ?? []);
  return sorted
    .map(
      (d) =>
        d.assets
          .filter(
            (a) =>
              a.tags.includes("COMPLIANT") &&
              a.tags.includes("LANDSCAPE") &&
              a.tags.includes("LARGE"),
          )
          .map((asset) => ({
            src: asset?.assetUrl,
            name: asset?.fileName,
            digitalSample: d,
          }))?.[0],
    )
    .filter((it): it is DigitalSampleImage => Boolean(it?.src));
}

function sortDigitalSamples(
  digitalSamples: Array<DigitalSample>,
): Array<DigitalSample> {
  return digitalSamples.slice().sort((a, b) => {
    if (a.materialFinish === "Glossy") {
      return -1;
    }
    if (b.materialFinish === "Glossy") {
      return 1;
    }
    return a.materialFinish.localeCompare(b.materialFinish);
  });
}
